import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ActivityTimeline } from "../../../components/activity-timeline/ActivityTimeline";
import { Drawer } from "../../../components/Drawer";
import { Flex } from "../../../components/Flex";
import { Spinner } from "../../../components/Spinner";
import { KanbanAxios } from "../../../axios/kanban/kanban.axios";
import { BaseActivity } from "../../../model/activity/BaseActivity";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import { useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { getTranslatedKanbanName } from "../KanbanUtils";
import { useDrawersContext } from "../../tasks/drawers-context";

/**
 * The pipeline's own history - renames, columns added and renamed - as opposed
 * to the per-card history in the card drawer.
 */
export const KanbanActivityDrawer: React.FC = () => {
  const { t } = useTranslation();
  const [activities, setActivities] = useState<BaseActivity[]>([]);
  const [loading, setLoading] = useState(false);

  const kanban = useAppSelector(kanbanSliceSelectors.selectUserCurrentKanban);
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const { kanbanActivityVisible, setKanbanActivityVisible } =
    useDrawersContext();

  const kanbanId = kanban?._id;

  const fetchActivities = useCallback(async () => {
    if (!kanbanId || !userId) {
      return;
    }

    try {
      setLoading(true);
      setActivities(
        await KanbanAxios.getKanbanActivities({ userId, kanbanId }),
      );
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [kanbanId, userId]);

  // Fetched on open rather than with the board: this is a rarely opened panel,
  // and its contents change independently of the cards.
  useEffect(() => {
    if (kanbanActivityVisible) {
      fetchActivities();
    } else {
      setActivities([]);
    }
  }, [kanbanActivityVisible, fetchActivities]);

  return (
    <Drawer
      key={kanbanId}
      open={kanbanActivityVisible}
      onClose={() => setKanbanActivityVisible(false)}
      title={`${t("common.activities")} - ${getTranslatedKanbanName(kanban?.name || "")}`}
      size="large"
    >
      {loading ? (
        <Flex justifyContent="center" padding="l">
          <Spinner color="main" />
        </Flex>
      ) : (
        <ActivityTimeline activities={activities} />
      )}
    </Drawer>
  );
};
